import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../../services/api';
import { TrendingUp, TrendingDown } from 'lucide-react';

/**
 * OilMarketPanel Component
 * Displays TradingView widgets for oil prices and stock indices
 */
const OilMarketPanel = () => {
  const [oilData, setOilData] = useState(null);

  useEffect(() => {
    const loadOilData = async () => {
      try {
        const res = await dashboardAPI.getOilPrices();
        setOilData(res.data);
      } catch (error) {
        console.error('Failed to load oil prices', error);
      }
    };

    loadOilData();
    const interval = setInterval(loadOilData, 15 * 60 * 1000); // Refresh every 15 minutes
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    if (price === 0) return 'Loading...';
    return `$${price.toFixed(2)}`;
  };

  const getChangeColor = (change) => {
    if (!change && change !== 0) return 'text-gray-500';
    return change >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';
  };

  return (
    <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Oil & Market Prices
      </h2>

      {/* Current Prices Display */}
      {oilData && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Brent Crude</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {formatPrice(oilData.current?.brent)}
            </div>
            <div className={`text-sm font-semibold flex items-center ${getChangeColor(oilData.changes?.brent24h)}`}>
              {oilData.changes?.brent24h >= 0 ? (
                <TrendingUp className="w-4 h-4 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1" />
              )}
              {oilData.changes?.brent24h ? `${oilData.changes.brent24h > 0 ? '+' : ''}${oilData.changes.brent24h.toFixed(2)}%` : 'N/A'}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">WTI Crude</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {formatPrice(oilData.current?.wti)}
            </div>
            <div className={`text-sm font-semibold flex items-center ${getChangeColor(oilData.changes?.wti24h)}`}>
              {oilData.changes?.wti24h >= 0 ? (
                <TrendingUp className="w-4 h-4 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1" />
              )}
              {oilData.changes?.wti24h ? `${oilData.changes.wti24h > 0 ? '+' : ''}${oilData.changes.wti24h.toFixed(2)}%` : 'N/A'}
            </div>
          </div>
        </div>
      )}

      {/* TradingView Widgets */}
      <div className="space-y-6">
        {/* Brent Crude Chart */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Brent Crude Oil (UKOIL)
          </div>
          <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <iframe
              src="https://www.tradingview.com/widgetembed/?symbol=UKOIL&interval=15&theme=dark&style=1&locale=en&backgroundColor=%231e1e1e&hide_top_toolbar=0&hide_legend=0&save_image=0&toolbar_bg=%231e1e1e&studies=%5B%5D&hide_volume=0"
              title="Brent Crude Chart"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {oilData?.changes?.brent24h !== undefined && oilData.changes.brent24h !== 0
              ? `Oil prices ${oilData.changes.brent24h > 0 ? 'up' : 'down'} ${Math.abs(oilData.changes.brent24h).toFixed(2)}% after Hormuz traffic changes`
              : 'Monitoring oil price movements in response to Middle East developments'}
          </p>
        </div>

        {/* WTI Crude Chart */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            WTI Crude Oil (USOIL)
          </div>
          <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <iframe
              src="https://www.tradingview.com/widgetembed/?symbol=USOIL&interval=15&theme=dark&style=1&locale=en&backgroundColor=%231e1e1e&hide_top_toolbar=0&hide_legend=0&save_image=0&toolbar_bg=%231e1e1e&studies=%5B%5D&hide_volume=0"
              title="WTI Crude Chart"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            WTI tracking global oil market volatility
          </p>
        </div>

        {/* Stock Indices */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Regional Stock Indices
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* S&P 500 */}
            <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <iframe
                src="https://www.tradingview.com/widgetembed/?symbol=SPX500&interval=15&theme=dark&style=1&locale=en&backgroundColor=%231e1e1e&hide_top_toolbar=0&hide_legend=0&save_image=0&toolbar_bg=%231e1e1e&studies=%5B%5D&hide_volume=0"
                title="S&P 500"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
            {/* NASDAQ */}
            <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <iframe
                src="https://www.tradingview.com/widgetembed/?symbol=NASDAQ&interval=15&theme=dark&style=1&locale=en&backgroundColor=%231e1e1e&hide_top_toolbar=0&hide_legend=0&save_image=0&toolbar_bg=%231e1e1e&studies=%5B%5D&hide_volume=0"
                title="NASDAQ"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
            {/* Gold (XAUUSD) */}
            <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <iframe
                src="https://www.tradingview.com/widgetembed/?symbol=XAUUSD&interval=15&theme=dark&style=1&locale=en&backgroundColor=%231e1e1e&hide_top_toolbar=0&hide_legend=0&save_image=0&toolbar_bg=%231e1e1e&studies=%5B%5D&hide_volume=0"
                title="Gold Price"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
            {/* US Dollar Index (DXY) */}
            <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <iframe
                src="https://www.tradingview.com/widgetembed/?symbol=DXY&interval=D&theme=dark&style=1&locale=en&backgroundColor=%231e1e1e&hide_top_toolbar=0&hide_legend=0&save_image=0&toolbar_bg=%231e1e1e&studies=%5B%5D&hide_volume=0"
                title="US Dollar Index"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Global markets, gold, and dollar responding to Middle East developments
          </p>
        </div>
      </div>
    </div>
  );
};

export default OilMarketPanel;

